// Import styles for Vite/Tailwind processing
import './input.css';
import './style.css';
import errorHandler from './handlers/ErrorHandler.js';
import { STORAGE_KEYS } from './constants/index.js';
import AuthClient from './clients/AuthClient.js';
import CacheManager from './utils/CacheManager.js';
import { cinematicNav, cinematicProductNav, updateDocumentTitle } from './utils/navigation.js';
import { initializeAnalytics, trackDeviceType } from './services/AnalyticsService.js';
import AnalyticsService from './services/AnalyticsService.js';

import './utils/firebase.js';
import { db, auth } from './utils/firebase.js';
import ko from 'knockout';
import './utils/koBindings.js';
import { isDev } from './utils/common.js';

import AppViewModel from './viewModels/AppViewModel.js';

import en from './locales/en.json';
import pl from './locales/pl.json';

const locales = { en, pl };

// ============================================================================
// Initialize window.Engryv namespace - centralize all global state
// ============================================================================
window.Engryv = {
    // Core services
    errorHandler: errorHandler,
    cacheManager: CacheManager,
    
    // i18n - translation function will be set below
    i18n: {
        t: null,
        setLang: null,
        currentLocale: 'en'
    },
    
    // App state
    app: null,
    appViewModel: null,
    
    // UI references  
    ui: {
        allowAnalyticsCookies: null
    },
    
    // Navigation
    navigation: {
        cinematicNav: cinematicNav,
        cinematicProductNav: cinematicProductNav,
        updateDocumentTitle: updateDocumentTitle
    },
    
    // Analytics
    initializeAnalytics: initializeAnalytics,
    AnalyticsService: AnalyticsService
};

/**
 * Determine user's locale based on hostname
 */
function getLocale() {
  // Determine locale by hostname
  if (window.location.hostname.endsWith('.pl')) {
    return 'pl'; // engryv.pl → Polish
  } else if (window.location.hostname.endsWith('.com') || window.location.hostname.endsWith('.eu')) {
    return 'en'; // engryv.com and engryv.eu → English (ignore browser language)
  }
  return 'en'; // Default to English
}

window.Engryv.i18n.currentLocale = getLocale();

/**
 * Translation function
 */
window.Engryv.i18n.t = function(key) {
  const keys = key.split('.');
  let value = locales[window.Engryv.i18n.currentLocale];
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      return key;
    }
  }
  return value;
};

/**
 * Set language function
 */
window.Engryv.i18n.setLang = function(lang) {
  if (!locales[lang]) return;
  window.Engryv.i18n.currentLocale = lang;
  document.documentElement.lang = lang;
  if (window.Engryv.appViewModel && typeof window.Engryv.appViewModel.currentLang === 'function') {
    window.Engryv.appViewModel.currentLang(lang);
  }
  // Reload the current view to update content
  if (window.Engryv.appViewModel && typeof window.Engryv.appViewModel.loadView === 'function') {
    window.Engryv.appViewModel.loadView(window.Engryv.appViewModel.currentViewSlug());
  }
};

/**
 * Setup Firebase Authentication with anonymous sign-in
 * Dispatches firebaseReady event when user is authenticated
 */
AuthClient.onAuthStateChanged(async (user) => {
  if (user) {
    // User is authenticated (anonymous or otherwise)
    document.dispatchEvent(new CustomEvent('firebaseReady', { detail: { db, auth } }));
  } else {
    // No user signed in, attempt anonymous sign-in
    try {
      await AuthClient.signInAnonymously();
      // onAuthStateChanged will fire again with the new user
    } catch (err) {
      console.error('Firebase Auth: Anonymous sign-in failed', err);
      // Dispatch event with error so app can handle it
      document.dispatchEvent(new CustomEvent('firebaseReady', { detail: { db, auth, error: err } }));
    }
  }
});

/**
 * Firebase initialization and application startup
 */
document.addEventListener('firebaseReady', (event) => {
    try {
        const { db: firebaseDb, auth: firebaseAuth } = event.detail;
        if (!firebaseDb || !firebaseAuth) {
            throw new Error('Firebase not ready: missing db or auth instance.');
        }
        // --- Set language before initializing ViewModel ---
        let initialLang = getLocale();
        window.Engryv.i18n.setLang(initialLang);
        // ---
        
        // Initialize app with new AppViewModel
        window.Engryv.app = {};
        window.Engryv.app.viewModel = new AppViewModel(firebaseDb, firebaseAuth);
        window.Engryv.app.errorHandler = window.Engryv.errorHandler;
        window.Engryv.appViewModel = window.Engryv.app.viewModel;
        // Also set as direct window property for easier access
        window.appViewModel = window.Engryv.app.viewModel;
        
        // Expose CacheManager for debugging in development only
        if (isDev) {
            window.CacheManager = window.Engryv.cacheManager;
        }
        
        // Store UI references  
        window.Engryv.ui.allowAnalyticsCookies = window.Engryv.appViewModel.allowAnalyticsCookies;
        
        // Expose navigation functions as globals for template binding access
        // Templates call cinematicNav() and cinematicProductNav() directly
        window.cinematicNav = window.Engryv.navigation.cinematicNav;
        window.cinematicProductNav = window.Engryv.navigation.cinematicProductNav;
        window.updateDocumentTitle = window.Engryv.navigation.updateDocumentTitle;
        
        // Handle cookies & analytics initialization
        // Initialize analytics if consent was already given (before AppViewModel loads)
        // Key must match STORAGE_KEYS.COOKIE_CONSENT from src/constants/index.js
        if (localStorage.getItem(STORAGE_KEYS.COOKIE_CONSENT) === 'accepted') {
          if (typeof window.enableAnalytics === 'function') {
            window.enableAnalytics();
          }
          // Also initialize the new analytics service
          initializeAnalytics();
          // Track device type on page load
          trackDeviceType();
        }
        
        // Store navigation reference
        window.Engryv.navigation.cinematicProductNav = window.Engryv.app.viewModel.loadView.bind(window.Engryv.app.viewModel);
        
        // Apply Knockout bindings
        ko.applyBindings(window.Engryv.app.viewModel);
        
        // Initialize DOM mutation observer for dynamic content binding
        // Re-applies Knockout bindings when main content is dynamically updated
        initializeDomObserver();
        
        function handleRouteChange() {
          // Support both /slug and #/slug for backward compatibility
          let slug = location.pathname.replace(/^\//, '');
          if (!slug || slug === '') slug = 'home';
          // If hash is present and matches a slug, prefer it (for legacy links)
          if (location.hash && location.hash.startsWith('#/')) {
            slug = location.hash.replace(/^#\/?/, '');
            // Clean up the URL to /slug (no reload)
            const newUrl = window.location.origin + '/' + slug;
            window.history.replaceState({}, '', newUrl);
          }
          window.Engryv.appViewModel.loadView(slug);
        }
        window.addEventListener('popstate', handleRouteChange);
        handleRouteChange();
    } catch (error) {
        errorHandler.displayCriticalError(error, { stage: 'Firebase Initialization' }, 'getAdminEmail');
    }
});

/**
 * Initialize a MutationObserver to re-apply Knockout bindings
 * when main content is dynamically updated
 */
function initializeDomObserver() {
    const mainContent = document.getElementById('main-content');
    if (!mainContent) return;
    
    const observer = new MutationObserver(() => {
        try {
            ko.cleanNode(mainContent);
            ko.applyBindingsToDescendants(window.Engryv.appViewModel, mainContent);
        } catch (e) {
            // Ignore errors (e.g., if already bound or during cleanup)
        }
    });
    
    observer.observe(mainContent, { childList: true, subtree: false });
}

const preloadIdeasImage = new Image();
preloadIdeasImage.src = 'images/ideas/Engryv_ideas_small.png';

// Service Worker registration (offline caching)
// Requires HTTPS - skipped during testing phase if SSL is unavailable
if ('serviceWorker' in navigator && window.location.protocol === 'https:') {
    window.addEventListener('load', function() {
        navigator.serviceWorker.register('/service-worker.js')
            .then(function(registration) {
                // Registered successfully
            })
            .catch(function(error) {
                // Service Worker registration failed
            });
    });
}
